import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { SelfApplyJobApplication } from 'src/app/recruiting/candidates/submittal/models/self-apply-job-application.model';
import { UsedByModule } from 'src/app/shared/models/enums/used-by-module.enum';
import { SubmittalJobSummary } from 'src/app/shared/models/submittal-job-summary.model';
import { SubmittalStatusReason } from 'src/app/shared/models/submittal-status-reason.model';
import { SubmittalStatus, SubmittalStatusWithLegacyTranslationDto } from 'src/app/shared/models/submittal-status.model';
import { combine } from 'src/app/shared/utilities/route';
import { CandidateSubmittalEdit } from './models/candidate-submittal-edit.model';
import { FacilitySearchResult } from './models/faciity-search-result.model';
import { LookupFacilityJob } from './models/lookup-facility-job.model';

@Injectable({
    providedIn: 'root'
})
export class SubmittalService {
    private readonly _coreApiPath: string;

    constructor(
        private readonly _httpClient: HttpClient,
        @Inject(APP_CONFIG) private readonly _settings: Settings
    ) {
        this._coreApiPath = combine(this._settings.coreAPIUrl, 'ayanova');
    }

    searchFacilities(token: string, perDiemOnly: boolean): Observable<FacilitySearchResult[]> {
        return this._httpClient.get<FacilitySearchResult[]>(
            `${this._coreApiPath}/facility?name=${encodeURIComponent(token)}&perDiemOnly=${perDiemOnly}`
        );
    }

    lookupFacilityJobs(facilityId: number): Observable<LookupFacilityJob[]> {
        return this._httpClient.get<LookupFacilityJob[]>(`${this._coreApiPath}/facility/jobs/${facilityId}`);
    }

    createCandidateSubmittal(submittal: CandidateSubmittalEdit): Observable<number> {
        const url = `${this._coreApiPath}/candidate/submittal`;
        return this._httpClient.post<number>(url, submittal);
    }

    saveCandidateSubmittalAttachment(fileToUpload: File, submittalId: number): Observable<boolean> {
        const url = `${this._coreApiPath}/candidate/${submittalId}/attachment`;
        const input = new FormData();
        input.append('file', fileToUpload);
        return this._httpClient.post<boolean>(url, input);
    }

    saveCandidateProfileSubmittalAttachments(ids: number[], submittalId: number): Observable<boolean> {
        const url = `${this._coreApiPath}/candidate/${submittalId}/profile-attachments`;
        return this._httpClient.post<boolean>(url, ids);
    }

    saveCandidateSubmittalAttachments(filesToUpload: File[], submittalId: number): Observable<boolean> {
        if (filesToUpload.length === 0) {
            return of(true);
        }

        const url = `${this._coreApiPath}/candidate/${submittalId}/attachments`;
        const input = new FormData();
        filesToUpload.forEach((f) => {
            input.append('file', f);
        });

        return this._httpClient.post<boolean>(url, input);
    }

    sendCandidateSubmittalEmails(candidateId: number, submittalId: number, forAm: boolean): Observable<boolean> {
        const url = `${this._coreApiPath}/candidate/${candidateId}/${submittalId}/send-emails?forAm=${forAm}`;
        return this._httpClient.get<boolean>(url);
    }

    getCandidatesSubmittals(userId: number, args: any, usedByModule: UsedByModule): Observable<SubmittalJobSummary[]> {
        let url = `${this._coreApiPath}/perdiem-recruiting/${userId}/submittal`;
        if (usedByModule === UsedByModule.StaffRecruiting) {
            url = `${this._coreApiPath}/candidate/${userId}/submittals`;
        }
        return this._httpClient.get<SubmittalJobSummary[]>(url);
    }

    updateCandidatesSubmittalStatus(candidateId: number, submittal: SubmittalJobSummary): Observable<null> {
        const url = `${this._coreApiPath}/candidate/${candidateId}/submittal`;
        return this._httpClient.put<null>(url, submittal);
    }

    deleteSubmittal(data: any, usedByModule: UsedByModule) {
        let url = `${this._coreApiPath}/perdiem-recruiting/submittal`;
        if (usedByModule === UsedByModule.StaffRecruiting) {
            url = `${this._coreApiPath}/recruiting/submittal`;
        }

        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: data
        };
        return this._httpClient.delete(url, options);
    }

    getSubmittalStatuses(): Observable<Array<SubmittalStatus>> {
        const url = `${this._coreApiPath}/job/lookup/job-submittal-statuses`;
        return this._httpClient.get<Array<SubmittalStatus>>(url);
    }

    getLegacySubmittalStatuses(): Observable<Array<SubmittalStatusWithLegacyTranslationDto>> {
        const url = `${this._coreApiPath}/job/lookup/legacy-job-submittal-statuses`;
        return this._httpClient.get<SubmittalStatusWithLegacyTranslationDto[]>(url);
    }

    getSubmittalStatusReasons(): Observable<Array<SubmittalStatusReason>> {
        const url = `${this._coreApiPath}/job/lookup/job-submittal-status-reasons`;
        return this._httpClient.get<Array<SubmittalStatusReason>>(url);
    }

    getSelfApplyApplications(candidateId: number, jobIds: number[]): Observable<SelfApplyJobApplication[]> {
        return this._httpClient.get<SelfApplyJobApplication[]>(`${this._coreApiPath}/SelfApplyJob/applications`, {
            params: { candidateId, jobIds }
        });
    }

    npiClearedForFacility(facilityId: number, npi: number): Observable<boolean> {
        return this._httpClient.post<boolean>(`${this._coreApiPath}/facilities/${facilityId}/npi-clear-status`, npi);
    }

    searchEligibleFacilitiesForSubmittal(name: string): Observable<FacilitySearchResult[]> {
        return this._httpClient.get<FacilitySearchResult[]>(
            `${this._coreApiPath}/submittal/facility/name-search?name=${encodeURIComponent(name)}`
        );
    }
}
